import React, { useState } from 'react';
import { Organization, PaginatedResponse } from '../../types';
import { 
  TrashIcon, 
  PencilIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAppDispatch } from '../../hooks';
import { showToast } from '../../store/slices/toastSlice';
import { getIncrementalId, getPaginationSizeOptions } from '../../utils/helpers';

interface OrganizationListProps {
  organizations: PaginatedResponse<Organization>;
  onEdit: (organization: Organization) => void;
  onDelete: (orgId: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  pageSize: number;
  currentUserRole?: string;
}

const OrganizationList: React.FC<OrganizationListProps> = ({
  organizations, 
  onEdit, 
  onDelete, 
  onPageChange, 
  onPageSizeChange,
  currentPage,
  pageSize,
  currentUserRole 
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState<number | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-primary-500/10 text-primary-500 dark:text-primary-400';
      case 'disabled':
        return 'bg-gray-500/10 text-gray-500 dark:text-gray-400';
      case 'suspended':
        return 'bg-yellow-500/10 text-yellow-500 dark:text-yellow-400';
      default:
        return 'bg-gray-500/10 text-gray-500 dark:text-gray-400';
    }
  };

  const handleDeleteClick = (org: Organization) => {
    setOrgToDelete(org);
  };

  const handleDeleteConfirm = async () => {
    if (orgToDelete) {
      try {
        await api.delete(`/organizations/${orgToDelete.org_id}`);
        dispatch(showToast({
          message: `Successfully deleted organization ${orgToDelete.name}`,
          type: 'success'
        }));
      } catch (err: any) {
        dispatch(showToast({
          message: err.response?.data?.detail || 'Failed to delete organization',
          type: 'error'
        }));
      }
      setOrgToDelete(null);
    }
  };

  const handleSubscriptionToggle = async (org: Organization) => {
    if (subscriptionLoading === org.org_id) return;
    
    setSubscriptionLoading(org.org_id);
    try {
      const response = await api.post(`/subscriptionsv2/organizations/${org.org_id}/toggle`);
      const result = response.data;
      
      dispatch(showToast({
        message: `Successfully ${result.new_status === 'active' ? 'activated' : 'deactivated'} subscription for ${org.name}`,
        type: 'success'
      }));
      
      // Update the organization in the list
      const updatedOrg = {
        ...org,
        subscription_status: result.new_status,
        subscription_updated_at: result.updated_at
      };
      onEdit(updatedOrg);
      
    } catch (err: any) {
      dispatch(showToast({
        message: err.response?.data?.detail || 'Failed to toggle subscription',
        type: 'error'
      }));
    } finally {
      setSubscriptionLoading(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-[1000px] w-full divide-y divide-light-border dark:divide-dark-border">
            <thead className="bg-light-card dark:bg-dark-card">
              <tr>
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  ID
                </th>
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  Name
                </th>
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  Domains
                </th>
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  Status
                </th>
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  Max Allowed Users
                </th>
                {currentUserRole === 'super_admin' && (
                  <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                    Trials
                  </th>
                )}
                {currentUserRole === 'super_admin' && (
                  <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                    Subscription
                  </th>
                )}
                <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-light-card dark:bg-dark-card divide-y divide-light-border dark:divide-dark-border">
              {organizations.items.map((org, index) => (
                <tr key={org.org_id} className="hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {getIncrementalId(organizations.items, index, currentUserRole)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{org.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {org.domains.map((domain, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-500/10 text-primary-500 dark:text-primary-400"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={org.status}
                      onChange={(e) => onEdit({ ...org, status: e.target.value })}
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(org.status)} bg-transparent border-none focus:ring-0 cursor-pointer`}
                    >
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/users?org_id=${org.org_id}`)}
                      className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-200"
                    >
                      <UsersIcon className="h-5 w-5" />
                      <span>{org.max_users}</span>
                    </button>
                  </td>
                  {currentUserRole === 'super_admin' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {org.web_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 dark:text-blue-400">
                            Web
                          </span>
                        )}
                        {org.ide_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500 dark:text-green-400">
                            IDE
                          </span>
                        )}
                        {org.security_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500 dark:text-red-400">
                            Security
                          </span>
                        )}
                        {org.devops_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500 dark:text-yellow-400">
                            DevOps
                          </span>
                        )}
                        {!org.web_trial && !org.ide_trial && !org.security_trial && !org.devops_trial && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">No trials</span>
                        )}
                      </div>
                    </td>
                  )}
                  {currentUserRole === 'super_admin' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSubscriptionToggle(org)}
                          disabled={subscriptionLoading === org.org_id}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            org.subscription_status === 'active'
                              ? 'bg-primary-500'
                              : 'bg-gray-200 dark:bg-gray-700'
                          } ${subscriptionLoading === org.org_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={`${org.subscription_status === 'active' ? 'Deactivate' : 'Activate'} subscription`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                              org.subscription_status === 'active' ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-xs font-medium ${
                          org.subscription_status === 'active'
                            ? 'text-primary-500 dark:text-primary-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {subscriptionLoading === org.org_id ? 'Loading...' : (
                            org.subscription_status === 'active' ? 'Active' : 'Inactive'
                          )}
                        </span>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => onEdit(org)}
                        className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                        title="Edit organization"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      {currentUserRole === 'super_admin' && (
                        <button
                          onClick={() => handleDeleteClick(org)}
                          className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                          title="Delete organization"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
      </div>

      {/* Pagination */}
      {organizations.total > 0 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {organizations.items.length > 0 ? (currentPage - 1) * organizations.size + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {Math.min(currentPage * organizations.size, organizations.total)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {organizations.total}
              </span>{' '}
              results
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="px-3 py-1 rounded-lg bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              >
                {getPaginationSizeOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                currentPage === 1
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
              }`}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === organizations.pages}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                currentPage === organizations.pages
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
              }`}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {orgToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-md bg-light-card dark:bg-dark-card rounded-xl shadow-lg p-6">
            <button
              onClick={() => setOrgToDelete(null)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/10">
                <TrashIcon className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                Delete Organization
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete {orgToDelete.name}? This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-center gap-4">
                <button
                  onClick={() => setOrgToDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationList;
